import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FC, useMemo, useState } from 'react';
import { ParagraphPassListMapFragment } from '../../../generated/types';
import Container from '../../Layout/Container';
import ParagraphTitle from '../ParagraphTitle';
import groupBy from 'lodash/groupBy';
import dynamic from 'next/dynamic';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { RiMap2Line } from '@react-icons/all-files/ri/RiMap2Line';
import { RiFileListLine } from '@react-icons/all-files/ri/RiFileListLine';
import List from './List';

interface PassListMapProps {
  data: ParagraphPassListMapFragment;
}

const Map = dynamic(() => import('../PassMap/Map'), {
  ssr: false,
});

const PassListMap: FC<PassListMapProps> = ({ data }) => {
  const [activeView, setActiveView] = useState<'list' | 'map'>('list');
  const [filterCountryIds, setFilterCountryIds] = useState<Array<number>>([]);
  const [filterRegionIds, setFilterRegionIds] = useState<Array<number>>([]);
  const [filterUnpaved, setFilterUnpaved] = useState<null | boolean>(null);
  const [filterToll, setFilterToll] = useState<null | boolean>(null);

  const filteredData = useMemo(() => {
    let allPois = data.data?.items;

    if (filterCountryIds.length > 0) {
      allPois = allPois.filter((poi) =>
        filterCountryIds.includes(poi.country?.id)
      );
    }

    if (filterRegionIds.length > 0) {
      allPois = allPois.filter((poi) =>
        filterRegionIds.includes(poi.region?.id)
      );
    }

    if (filterUnpaved !== null) {
      allPois = allPois.filter((poi) => {
        if (filterUnpaved === true) {
          return poi.unpaved === true;
        } else {
          return poi.unpaved === null || poi.unpaved === false;
        }
      });
    }

    if (filterToll !== null) {
      allPois = allPois.filter((poi) => {
        if (filterToll === true) {
          return poi.toll === true;
        } else {
          return poi.toll === null || poi.toll === false;
        }
      });
    }

    return allPois;
  }, [data, filterCountryIds, filterRegionIds, filterUnpaved, filterToll]);

  const countries = useMemo(() => {
    const countries: Array<{
      id: number;
      name: string;
    }> = [
      {
        id: 0,
        name: 'Global',
      },
    ];

    data.data.items?.forEach((item) => {
      if (item.country) {
        const hasAlready = countries.find((c) => c.id === item.country.id);

        if (!hasAlready) {
          countries.push({
            id: item.country.id,
            name: item.country.name,
          });
        }
      }
    });

    return countries.sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const regions = useMemo(() => {
    const regions: Array<{
      country: string;
      regions: Array<{
        id: number;
        name: string;
      }>;
    }> = [];

    const groups = groupBy(data.data.items, (item) => {
      return item.country?.id || 0;
    });

    for (const [countryId, poisInCountry] of Object.entries(groups)) {
      const groupedRegions = groupBy(
        poisInCountry,
        (item) => item.region?.id || 0
      );

      const countryData = {
        country: poisInCountry[0].country?.name || 'Global',
        regions: [],
      };

      for (const [regionId, poisInRegion] of Object.entries(groupedRegions)) {
        countryData.regions.push({
          id: poisInRegion[0].region?.id || 0,
          name: poisInRegion[0].region?.name || 'Keine Region',
        });
      }

      countryData.regions = countryData.regions.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      regions.push(countryData);
    }

    regions.sort((a, b) => a.country.localeCompare(b.country));

    return regions;
  }, [data]);

  return (
    <Container>
      {data.title && <ParagraphTitle label={data.title} />}

      <Flex>
        <Button
          variant="ghost"
          onClick={() => setActiveView('list')}
          isActive={activeView === 'list'}
          size="sm"
        >
          <Icon as={RiFileListLine} mr={1} />
          Listenansicht
        </Button>
        <Button
          variant="ghost"
          onClick={() => setActiveView('map')}
          isActive={activeView === 'map'}
          size="sm"
        >
          <Icon as={RiMap2Line} mr={1} />
          Kartenansicht
        </Button>
      </Flex>

      <Wrap mt={2} alignItems="center" pos="relative" zIndex={3000}>
        <WrapItem>
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="ghost"
              rightIcon={<ChevronDownIcon />}
            >
              Länder
              {filterCountryIds.length > 0
                ? ` (${filterCountryIds.length} ausgewählt)`
                : ''}
            </MenuButton>
            <MenuList>
              {countries.map((countryData) => {
                return (
                  <MenuItem
                    key={countryData.id}
                    onClick={(e) => {
                      e.preventDefault();

                      if (filterCountryIds.includes(countryData.id)) {
                        setFilterCountryIds(
                          filterCountryIds.filter((i) => i !== countryData.id)
                        );
                      } else {
                        setFilterCountryIds([
                          ...filterCountryIds,
                          countryData.id,
                        ]);
                      }
                    }}
                  >
                    <Checkbox
                      width="100%"
                      isChecked={filterCountryIds.includes(countryData.id)}
                    >
                      {countryData.name}
                    </Checkbox>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        </WrapItem>

        <WrapItem>
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="ghost"
              rightIcon={<ChevronDownIcon />}
            >
              Regionen
              {filterRegionIds.length > 0
                ? ` (${filterRegionIds.length} ausgewählt)`
                : ''}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              {regions.map((regionData, k) => {
                return (
                  <MenuGroup key={k} title={regionData.country}>
                    {regionData.regions.map((region, i) => {
                      return (
                        <MenuItem
                          key={i}
                          onClick={(e) => {
                            e.preventDefault();

                            if (filterRegionIds.includes(region.id)) {
                              setFilterRegionIds(
                                filterRegionIds.filter((i) => i !== region.id)
                              );
                            } else {
                              setFilterRegionIds([
                                ...filterRegionIds,
                                region.id,
                              ]);
                            }
                          }}
                        >
                          <Checkbox
                            width="100%"
                            isChecked={filterRegionIds.includes(region.id)}
                          >
                            {region.name}
                          </Checkbox>
                        </MenuItem>
                      );
                    })}
                  </MenuGroup>
                );
              })}
            </MenuList>
          </Menu>
        </WrapItem>

        <WrapItem>
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="ghost"
              rightIcon={<ChevronDownIcon />}
            >
              Unbefestigte Straßen
              {filterUnpaved !== null
                ? ` (${filterUnpaved ? 'Ja' : 'Nein'})`
                : ''}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setFilterUnpaved(null);
                }}
              >
                <Checkbox width="100%" isChecked={filterUnpaved === null}>
                  Alle
                </Checkbox>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setFilterUnpaved(true);
                }}
              >
                <Checkbox width="100%" isChecked={filterUnpaved === true}>
                  Ja
                </Checkbox>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setFilterUnpaved(false);
                }}
              >
                <Checkbox width="100%" isChecked={filterUnpaved === false}>
                  Nein
                </Checkbox>
              </MenuItem>
            </MenuList>
          </Menu>
        </WrapItem>

        <WrapItem>
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="ghost"
              rightIcon={<ChevronDownIcon />}
            >
              Maut
              {filterToll !== null ? ` (${filterToll ? 'Ja' : 'Nein'})` : ''}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setFilterToll(null);
                }}
              >
                <Checkbox width="100%" isChecked={filterToll === null}>
                  Alle
                </Checkbox>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setFilterToll(true);
                }}
              >
                <Checkbox width="100%" isChecked={filterToll === true}>
                  Ja
                </Checkbox>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setFilterToll(false);
                }}
              >
                <Checkbox width="100%" isChecked={filterToll === false}>
                  Nein
                </Checkbox>
              </MenuItem>
            </MenuList>
          </Menu>
        </WrapItem>

        <WrapItem>
          <Box ml="auto">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setFilterRegionIds([]);
                setFilterCountryIds([]);
                setFilterUnpaved(null);
                setFilterToll(null);
              }}
            >
              Alle Filter zurücksetzen
            </Button>
          </Box>
        </WrapItem>
      </Wrap>

      {activeView === 'list' && <List pois={filteredData} />}
      {activeView === 'map' && <Map data={filteredData} />}
    </Container>
  );
};

export default PassListMap;
